import { useEffect, useMemo, useRef, useState } from 'react';
import { App, Button, Checkbox, Modal } from 'antd';
import { CheckOutlined, CloseOutlined, LoadingOutlined, LockOutlined } from '@ant-design/icons';
import { useLoginWithPasskey, usePrivy } from '@privy-io/react-auth';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage, useAuth } from '../../../auth/AuthContext';
import {
    calculatePromo,
    formatPrice,
    getAvailableDenoms,
    getDenomPlanLabel,
    getProductDescription,
    getProductDisplayName,
    getProductLogo,
    isEsimProduct,
    type CatalogProduct,
} from '../catalog';
import { AppApc } from '../../../api/appApc';
import { formatDateTime, formatNumber } from '../../../i18n/format';

type Step = 'pick' | 'review' | 'processing' | 'success' | 'failed';

const ORDER_POLL_MS = 1000;
const SUCCESS_ORDER_STATUSES = new Set(['delivered']);
const FAILED_ORDER_STATUSES = new Set([
    'fail',
    'failed',
    'failure',
    'error',
    'rejected',
    'cancelled',
    'canceled',
]);

function readRecord(value: unknown): Record<string, unknown> | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    return value as Record<string, unknown>;
}

function readOrderPayload(body: unknown) {
    const root = readRecord(body);
    if (!root) return null;
    if (Array.isArray(root.data)) return readRecord(root.data[0]);
    return readRecord(root.data) ?? root;
}

function readOrderId(payload: Record<string, unknown> | null) {
    if (!payload) return '';

    const direct = payload.id ?? payload.orderId;
    if (typeof direct === 'string' || typeof direct === 'number') return String(direct);

    const nested = readRecord(payload.order);
    const nestedId = nested?.id ?? nested?.orderId;
    if (typeof nestedId === 'string' || typeof nestedId === 'number') return String(nestedId);

    return '';
}

function readOrderStatus(payload: Record<string, unknown> | null) {
    if (!payload) return '';

    const direct = payload.status ?? payload.state;
    if (typeof direct === 'string' || typeof direct === 'number') {
        return String(direct).trim().toLowerCase();
    }

    const nested = readRecord(payload.order);
    const nestedStatus = nested?.status ?? nested?.state;
    if (typeof nestedStatus === 'string' || typeof nestedStatus === 'number') {
        return String(nestedStatus).trim().toLowerCase();
    }

    return '';
}

function delay(ms: number) {
    return new Promise((resolve) => {
        window.setTimeout(resolve, ms);
    });
}

type QuoteCashback = {
    cashbackVoucherId?: string;
    cashbackVoucherCode?: string;
    campaignId?: string;
    campaignName?: string;
    quotedCashbackMaxCredit?: string;
    quotedCashbackMaxCreditAtomic?: string;
    effectiveCostCredit?: string;
    effectiveCostCreditAtomic?: string;
    availabilityNote?: string;
};

type VoucherQuote = {
    quoteId: string;
    productId: string;
    faceValue: string;
    faceCurrency: string;
    quantity: number;
    customerTotalUsdt: string;
    networkFeeUsdt: string;
    expiresAt: string;
    termsVersion: string;
    cashback?: QuoteCashback;
} & QuoteCashback;

function getQuoteCashback(quote: VoucherQuote | null): QuoteCashback | null {
    if (!quote) return null;

    const nested = isPlainCashback(quote.cashback) ? quote.cashback : null;
    const source = nested ? { ...quote, ...nested } : quote;
    const hasCashback = Boolean(
        source.cashbackVoucherId ||
            source.cashbackVoucherCode ||
            source.quotedCashbackMaxCredit ||
            source.campaignName
    );

    return hasCashback ? source : null;
}

function isPlainCashback(value: unknown): value is QuoteCashback {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

type PurchaseModalProps = {
    product: CatalogProduct | null;
    country: string;
    availableCredits: number;
    displayName?: string;
    recipientEmail?: string;
    initialStep?: Step;
    initialAmount?: number;
    initialQuantity?: number;
    onClose: () => void;
};

function PurchaseModal({
    product,
    country,
    availableCredits,
    displayName,
    recipientEmail,
    initialStep = 'pick',
    initialAmount,
    initialQuantity = 1,
    onClose,
}: PurchaseModalProps) {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation('explore');
    const { message } = App.useApp();
    const { refreshProfile } = useAuth();
    const { ready, user } = usePrivy();
    const { loginWithPasskey, state: passkeyState } = useLoginWithPasskey();
    const pendingPurchase = useRef<{
        quoteId: string;
        acceptedTermsVersion: string;
        recipientEmail: string;
        code: string | null;
    } | null>(null);
    const purchaseStarted = useRef(false);
    const orderPollRef = useRef(0);
    const passkeyIds =
        user?.linkedAccounts.flatMap((account) =>
            account.type === 'passkey' ? [account.credentialId] : []
        ) ?? [];
    const hasPasskey = passkeyIds.length > 0;
    const [step, setStep] = useState<Step>(initialStep);
    const [amount, setAmount] = useState(initialAmount ?? 0);
    const [quantity, setQuantity] = useState(initialQuantity);
    const [promoInput, setPromoInput] = useState('');
    const [promoCode, setPromoCode] = useState<string | null>(null);
    const [promoError, setPromoError] = useState<string | null>(null);
    const [usedCodes, setUsedCodes] = useState<string[]>([]);
    const [consent, setConsent] = useState(false);
    const [confirmed, setConfirmed] = useState<ReturnType<typeof calculatePromo> | null>(null);
    const [quote, setQuote] = useState<VoucherQuote | null>(null);
    const [quoteLoading, setQuoteLoading] = useState(false);
    const [purchaseLoading, setPurchaseLoading] = useState(false);

    const language = i18n.resolvedLanguage || i18n.language;
    const denoms = useMemo(
        () => (product ? getAvailableDenoms(product) : []),
        [product]
    );
    const selectedDenom = denoms.find((item) => item.value === amount);
    const planLabel =
        product && isEsimProduct(product) && selectedDenom
            ? getDenomPlanLabel(selectedDenom, language)
            : '';
    const productName = product ? getProductDisplayName(product) : '';
    const productDescription = product ? getProductDescription(product, language) : '';
    const logoUrl = product ? getProductLogo(product) : '';
    const regionLabel = country || product?.regionName;
    const currency = denoms[0]?.currency || product?.currency || '';
    useEffect(() => {
        if (!product?.id || amount <= 0 || quantity <= 0 || !currency) return;

        let cancelled = false;
        setQuote(null);
        setQuoteLoading(true);

        const payload: {
            productId: string;
            faceValue: number;
            faceCurrency: string;
            quantity: number;
            cashbackVoucherCode?: string;
        } = {
            productId: product.id,
            faceValue: amount,
            faceCurrency: currency,
            quantity,
        };

        if (promoCode) {
            payload.cashbackVoucherCode = promoCode;
        }

        AppApc.PurchaseProduct(payload)
            .then((response) => {
                if (!cancelled && response.status === 200) {
                    setQuote(response.data?.data as VoucherQuote);
                }
            })
            .catch((error) => {
                if (!cancelled) {
                    console.error('Error getting voucher quote', error);
                    if (promoCode) {
                        setPromoError(t('promo.errors.unavailable'));
                        setPromoCode(null);
                    } else {
                        message.error(t('purchase.errors.quoteFailed'));
                    }
                }
            })
            .finally(() => {
                if (!cancelled) setQuoteLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [product?.id, amount, currency, quantity, promoCode, message, t]);

    useEffect(() => {
        return () => {
            orderPollRef.current += 1;
        };
    }, []);

    useEffect(() => {
        orderPollRef.current += 1;
        if (!product) return;
        const nextDenoms = getAvailableDenoms(product);
        const matched = nextDenoms.find((item) => item.value === initialAmount);
        setStep(initialStep);
        setAmount(matched?.value ?? nextDenoms[1]?.value ?? nextDenoms[0]?.value ?? 0);
        setQuantity(initialQuantity);
        setPromoInput('');
        setPromoCode(null);
        setPromoError(null);
        setConsent(false);
        setConfirmed(null);
        setPurchaseLoading(false);
        pendingPurchase.current = null;
        purchaseStarted.current = false;
    }, [product]);

    const subtotal = amount * quantity;
    const points = Math.max(0, Math.round(subtotal));
    const price = useMemo(
        () => calculatePromo(subtotal, promoCode, { displayName, usedCodes }),
        [subtotal, promoCode, displayName, usedCodes]
    );
    const quotedTotal = Number(quote?.customerTotalUsdt);
    const quotedFee = Number(quote?.networkFeeUsdt);
    const total = Number.isFinite(quotedTotal) ? quotedTotal : price.total;
    const fee = Number.isFinite(quotedFee) ? quotedFee : 0;
    const voucherPrice = Math.max(0, total - fee);
    const cashback = getQuoteCashback(quote);
    const cashbackCredit = Number(cashback?.quotedCashbackMaxCredit);
    const effectiveCost = Number(cashback?.effectiveCostCredit);

    const balanceAfter = availableCredits - total;
    const canPay = consent && balanceAfter >= 0 && Boolean(quote) && !quoteLoading;

    const applyPromo = () => {
        const raw = promoInput.trim();
        if (!raw) {
            setPromoError(t('promo.errors.enterCode'));
            return;
        }

        setPromoError(null);
        setPromoCode(raw);
        setPromoInput('');
    };

    const waitForOrder = async (orderId: string, session: number) => {
        while (orderPollRef.current === session) {
            const started = Date.now();

            try {
                const response = await AppApc.GetOrderDetail(orderId);
                if (orderPollRef.current !== session) return 'cancelled' as const;

                const status = readOrderStatus(readOrderPayload(response.data));
                if (SUCCESS_ORDER_STATUSES.has(status)) return 'success' as const;
                if (FAILED_ORDER_STATUSES.has(status)) return 'failed' as const;
            } catch (error) {
                if (orderPollRef.current !== session) return 'cancelled' as const;
                console.error('Error polling order detail', error);
            }

            const remaining = ORDER_POLL_MS - (Date.now() - started);
            if (remaining > 0 && orderPollRef.current === session) {
                await delay(remaining);
            }
        }

        return 'cancelled' as const;
    };

    const sendPurchase = async () => {
        const pending = pendingPurchase.current;
        if (!pending || purchaseStarted.current || !product) return;

        purchaseStarted.current = true;
        const session = ++orderPollRef.current;
        setStep('processing');
        try {
            const response = await AppApc.BuyPurchaseVoucher({
                quoteId: pending.quoteId,
                acceptedTermsVersion: pending.acceptedTermsVersion,
                recipientEmail: pending.recipientEmail,
            });

            if (orderPollRef.current !== session) return;

            if (response.status !== 200 && response.status !== 201) {
                throw new Error(t('purchase.errors.failed'));
            }

            const orderId = readOrderId(readOrderPayload(response.data));
            if (!orderId) {
                throw new Error(t('purchase.errors.missingOrderId'));
            }

            const result = await waitForOrder(orderId, session);
            if (result === 'cancelled' || orderPollRef.current !== session) return;

            if (result === 'success') {
                await refreshProfile();
                if (orderPollRef.current !== session) return;
                message.success(t('purchase.messages.success'));
                setStep('success');
                if (pending.code) {
                    setUsedCodes((codes) => [...codes, pending.code as string]);
                }
                return;
            }

            void refreshProfile();
            setStep('failed');
            message.error(t('purchase.errors.orderFailed'));
        } catch (error) {
            if (orderPollRef.current !== session) return;
            setStep('review');
            message.error(getApiErrorMessage(error, t('purchase.errors.completeFailed')));
        } finally {
            if (orderPollRef.current === session) {
                pendingPurchase.current = null;
                setPurchaseLoading(false);
            }
        }
    };

    useEffect(() => {
        if (!pendingPurchase.current || purchaseStarted.current) return;

        if (passkeyState.status === 'submitting-response' || passkeyState.status === 'done') {
            void sendPurchase();
        }
    }, [passkeyState.status]);

    const goToPasskeySetup = () => {
        onClose();
        navigate('/account');
    };

    const confirmPurchase = async () => {
        if (!canPay || !product || !quote || purchaseLoading || !ready) return;
        if (!recipientEmail) {
            message.error(t('purchase.errors.missingEmail'));
            return;
        }
        if (!hasPasskey) {
            goToPasskeySetup();
            return;
        }

        pendingPurchase.current = {
            quoteId: quote.quoteId,
            acceptedTermsVersion: quote.termsVersion,
            recipientEmail,
            code: price.code,
        };
        purchaseStarted.current = false;
        setPurchaseLoading(true);
        setConfirmed({ ...price, total });

        try {
            await loginWithPasskey({ credentialIds: passkeyIds });
            await sendPurchase();
        } catch (error) {
            if (purchaseStarted.current) return;

            console.error('Passkey verification failed', error);
            pendingPurchase.current = null;
            message.error(getApiErrorMessage(error, t('purchase.errors.passkey')));
            setPurchaseLoading(false);
        }
    };

    const title =
        step === 'review'
            ? t('purchase.titles.review')
            : step === 'processing'
                ? t('purchase.titles.processing')
            : step === 'success'
                ? t('purchase.titles.ready')
                : step === 'failed'
                    ? t('purchase.titles.failed')
                    : product
                        ? t('purchase.titles.product', { productName })
                        : '';

    const formatDenomLabel = (value: number) => {
        return `${formatNumber(value)} ${currency}`.trim();
    };

    const purchaseFields = (
        <div className="explore-buy__purchase-summary">
            <div className="explore-buy__row">
                <span>{t('purchase.amount')}</span>
                <span>
                    {formatDenomLabel(amount)}
                    {planLabel ? ` · ${planLabel}` : ''}
                </span>
            </div>
            <div className="explore-buy__row">
                <span>{t('purchase.quantity')}</span>
                <span>{formatNumber(quantity)}</span>
            </div>
            <div className="explore-buy__row">
                <span>{t('purchase.estimatedPrice')}</span>
                <div className="explore-buy__estimate explore-buy__estimate--summary">
                    <strong>
                        {quoteLoading
                            ? t('purchase.updating')
                            : t('purchase.creditAmount', {
                                  count: total,
                                  amount: formatPrice(total),
                              })}
                    </strong>
                </div>
            </div>
        </div>
    );

    const buyNowButton = (onBuy: () => void, requireConsent = false) => (
        <Button
            type="primary"
            className={balanceAfter >= 0 ? 'explore-buy__now' : 'explore-buy__submit'}
            disabled={
                quoteLoading ||
                purchaseLoading ||
                !quote ||
                balanceAfter < 0 ||
                (requireConsent && !ready) ||
                (requireConsent && hasPasskey && !consent)
            }
            loading={quoteLoading || (requireConsent && (!ready || purchaseLoading))}
            icon={balanceAfter < 0 ? <LockOutlined /> : undefined}
            onClick={onBuy}
        >
            {purchaseLoading && requireConsent
                ? t('purchase.purchasing')
                : quoteLoading
                ? t('purchase.updatingPrice')
                : balanceAfter < 0
                  ? t('purchase.insufficientBalance')
                  : requireConsent && !hasPasskey
                    ? t('purchase.addPasskey')
                    : requireConsent
                      ? t('purchase.confirmPasskey')
                      : t('actions.buyNow')}
        </Button>
    );

    return (
        <Modal
            open={Boolean(product)}
            onCancel={onClose}
            footer={null}
            centered
            className="topup-modal explore-buy-modal"
            width={560}
            title={title}
            destroyOnClose
        >
            {product && step === 'pick' ? (
                <div className="explore-buy">
                    <div className="explore-buy__hero">
                        <div
                            className={`explore-buy__hero-logo${
                                logoUrl ? ' explore-buy__hero-logo--image' : ''
                            }`}
                        >
                            {logoUrl ? (
                                <img src={logoUrl} alt="" />
                            ) : (
                                <span>{productName.charAt(0)}</span>
                            )}
                        </div>
                        <div className="explore-buy__hero-copy">
                            <strong>{productName}</strong>
                            <span>{regionLabel}</span>
                            <em>{formatDenomLabel(amount)}</em>
                        </div>
                    </div>
                    {productDescription ? <p className="explore-buy__desc">{productDescription}</p> : null}
                    <div className="explore-buy__row">
                        <span>{t('product.useIn')}</span>
                        <span>{regionLabel}</span>
                    </div>
                    {purchaseFields}
                    {buyNowButton(() => {
                        setConsent(false);
                        setStep('review');
                    })}
                </div>
            ) : null}

            {product && step === 'review' ? (
                <div className="explore-buy">
                    <div className="ticket-row">
                        {logoUrl ? (
                            <img
                                className="explore-buy__brand-logo explore-buy__brand-logo--image"
                                src={logoUrl}
                                alt=""
                            />
                        ) : (
                            <span className="brand-icon">{productName.charAt(0)}</span>
                        )}
                        <span className="label">
                            <strong>
                                {t('purchase.giftCardName', { productName })} ·{' '}
                                {quantity > 1
                                    ? t('purchase.quantityTimesAmount', {
                                          quantity: formatNumber(quantity),
                                          amount: formatDenomLabel(amount),
                                      })
                                    : formatDenomLabel(amount)}
                            </strong>
                            <small>
                                {regionLabel} · {t('product.digitalDelivery')}
                            </small>
                        </span>
                    </div>
                    {purchaseFields}
                    <div className="explore-buy__promo">
                        <label htmlFor="promo-code">{t('promo.label')}</label>
                        {promoCode ? (
                            <div className="explore-buy__applied">
                                <span>
                                    <CheckOutlined /> {t('promo.applied', { code: promoCode })}
                                </span>
                                <button
                                    type="button"
                                    className="text-button"
                                    onClick={() => {
                                        setPromoCode(null);
                                        setPromoError(null);
                                    }}
                                >
                                    {t('promo.remove')}
                                </button>
                            </div>
                        ) : (
                            <div className="explore-buy__promo-input">
                                <input
                                    id="promo-code"
                                    maxLength={32}
                                    placeholder={t('promo.placeholder')}
                                    autoComplete="off"
                                    autoCapitalize="characters"
                                    aria-invalid={Boolean(promoError)}
                                    value={promoInput}
                                    onChange={(event) => {
                                        setPromoInput(event.target.value);
                                        if (promoError) setPromoError(null);
                                    }}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter') applyPromo();
                                    }}
                                />
                                <button type="button" onClick={applyPromo}>
                                    {t('promo.apply')}
                                </button>
                            </div>
                        )}
                        {promoError ? <p className="explore-buy__error">{promoError}</p> : null}
                        {cashback && !quoteLoading ? (
                            <div className="explore-buy__cashback">
                                {cashback.campaignName ? (
                                    <div className="explore-buy__row">
                                        <span>{t('promo.cashback.campaign')}</span>
                                        <span>{cashback.campaignName}</span>
                                    </div>
                                ) : null}
                                {Number.isFinite(cashbackCredit) ? (
                                    <div className="explore-buy__row explore-buy__row--save">
                                        <span>{t('promo.cashback.maxCredit')}</span>
                                        <strong>
                                            +
                                            {t('purchase.creditAmount', {
                                                count: cashbackCredit,
                                                amount: formatPrice(cashbackCredit),
                                            })}
                                        </strong>
                                    </div>
                                ) : null}
                                
                            </div>
                        ) : null}
                    </div>
                    <div className="explore-buy__row">
                        <span>{t('purchase.giftCardPrice')}</span>
                        <span>
                            {t('purchase.creditAmount', {
                                count: voucherPrice,
                                amount: formatPrice(voucherPrice),
                            })}
                        </span>
                    </div>
                    <div className="explore-buy__row">
                        <span>{t('purchase.purchaseFee')}</span>
                        <span>
                            {t('purchase.creditAmount', {
                                count: fee,
                                amount: formatPrice(fee),
                            })}
                        </span>
                    </div>

                    {price.discount ? (
                        <div className="explore-buy__row explore-buy__row--save">
                            <span>{t('promo.discount', { code: price.code })}</span>
                            <strong>
                                −
                                {t('purchase.creditAmount', {
                                    count: price.discount,
                                    amount: formatPrice(price.discount),
                                })}
                            </strong>
                        </div>
                    ) : null}

                    <div className="explore-buy__row">
                        <span>{t('purchase.payWith')}</span>
                        <span>{t('purchase.apcCredits')}</span>
                    </div>
                    <div className="explore-buy__row explore-buy__row--total">
                        <span>{t('purchase.total')}</span>
                        <strong>
                            {t('purchase.creditAmount', {
                                count: total,
                                amount: formatPrice(total),
                            })}
                        </strong>
                    </div>

                    <div className="explore-buy__notice">
                        {t('purchase.giftCardValue', {
                            value:
                                quantity > 1
                                    ? t('purchase.quantityTimesAmount', {
                                          quantity: formatNumber(quantity),
                                          amount: formatDenomLabel(amount),
                                      })
                                    : formatDenomLabel(amount),
                        })}
                        <br />
                        {t('purchase.providerNotice', { region: regionLabel })}
                        {quote ? (
                            <>
                                <br />
                                {t('purchase.quoteExpiry', {
                                    date: formatDateTime(quote.expiresAt),
                                    version: quote.termsVersion,
                                })}
                            </>
                        ) : null}
                    </div>

                    <label className="explore-buy__consent">
                        <Checkbox checked={consent} onChange={(event) => setConsent(event.target.checked)}>
                            {t('purchase.consent')}
                        </Checkbox>
                    </label>

                    <p className="explore-buy__notice">
                        {ready && !hasPasskey
                            ? t('purchase.passkeyRequired')
                            : t('purchase.passkeyLead')}
                    </p>

                    {buyNowButton(ready && !hasPasskey ? goToPasskeySetup : confirmPurchase, true)}
                </div>
            ) : null}

            {product && step === 'processing' ? (
                <div className="explore-buy explore-buy--center">
                    <div className="explore-buy__status explore-buy__status--pulse">
                        <LoadingOutlined spin />
                    </div>
                    <h3>{t('purchase.processingHeading', { productName })}</h3>
                    <p>
                        {t('purchase.creditsReserved', {
                            count: (confirmed ?? price).total,
                            amount: formatPrice((confirmed ?? price).total),
                        })}
                    </p>
                    {(confirmed ?? price).code ? (
                        <div className="explore-buy__notice">
                            {t('purchase.promoReserved', {
                                code: (confirmed ?? price).code,
                                amount: formatPrice((confirmed ?? price).discount),
                            })}
                        </div>
                    ) : null}
                    <div className="explore-buy__notice">
                        {t('purchase.simulating')}
                    </div>
                </div>
            ) : null}

            {product && step === 'success' ? (
                <div className="explore-buy explore-buy--center">
                    <div className="explore-buy__status">
                        <CheckOutlined />
                    </div>
                    <h3>{t('purchase.successHeading', { productName })}</h3>
                    <p>{t('purchase.previewNotice')}</p>
                    <Button
                        type="primary"
                        className="explore-buy__submit"
                        onClick={() => {
                            onClose();
                            navigate('/vouchers');
                        }}
                    >
                        {t('actions.viewVouchers')}
                    </Button>
                    <Button
                        className="explore-buy__submit explore-buy__submit--secondary"
                        onClick={() => {
                            message.success(t('purchase.messages.ready'));
                            onClose();
                        }}
                    >
                        {t('actions.done')}
                    </Button>
                </div>
            ) : null}

            {product && step === 'failed' ? (
                <div className="explore-buy explore-buy--center">
                    <div className="explore-buy__status explore-buy__status--failed">
                        <CloseOutlined />
                    </div>
                    <h3>{t('purchase.failedHeading', { productName })}</h3>
                    <p>{t('purchase.failedNotice')}</p>
                    <Button
                        type="primary"
                        className="explore-buy__submit"
                        onClick={() => {
                            purchaseStarted.current = false;
                            setStep('review');
                        }}
                    >
                        {t('actions.tryAgain')}
                    </Button>
                    <Button
                        className="explore-buy__submit explore-buy__submit--secondary"
                        onClick={onClose}
                    >
                        {t('actions.done')}
                    </Button>
                </div>
            ) : null}
        </Modal>
    );
}

export default PurchaseModal;
